import { useState } from "react";

import { useHistory } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";

import useSegmentTrackEvent from "../../../utils/useSegmentTrackEvent";

type LocationState = {
  filterParam?: {
    category: string;
    value: string;
  };
};

type MenuButtonProps = {
  items: {
    title: string;
    path?: string;
    eventName: string;
    description: string;
    locationState?: LocationState;
    onClick?: () => void;
  }[];
  buttonText?: string;
  disabled?: boolean;
  tooltip?: string;
};

export const MenuButton = ({ items, disabled, tooltip, buttonText }: MenuButtonProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { trackEvent } = useSegmentTrackEvent();
  const history = useHistory();

  const handleMenuItemClick = (item: {
    title: string;
    path?: string;
    eventName: string;
    description: string;
    locationState?: LocationState;
    onClick?: () => void;
  }) => {
    trackEvent(item.eventName, {
      feature: "Home Dashboard",
      source: "Home Dashboard",
      target_internal_destination_description: item.description,
    });

    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      history.push(item.path, item.locationState);
    }

    setAnchorEl(null);
  };

  if (items.length === 1) {
    return (
      <Tooltip title={tooltip}>
        <span>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            endIcon={<ArrowForwardIcon />}
            sx={{ width: "fit-content", "@media print": { display: "none" } }}
            onClick={() => {
              handleMenuItemClick(items[0]);
            }}
            data-cy="security-insights-action-button"
          >
            {items[0].title}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            sx={{ width: "fit-content", "@media print": { display: "none" } }}
            data-cy="cloud-waste-review-action-btn"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(event) => {
              setAnchorEl(anchorEl ? null : (event.currentTarget as HTMLElement));
            }}
          >
            {buttonText || "Review and take action"}
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setAnchorEl(null);
        }}
        data-cy="cloud-waste-actions-menu"
        MenuListProps={{
          "aria-labelledby": "cloud-waste-review-action-btn",
        }}
        sx={{ "@media print": { display: "none" } }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.title}
            onClick={() => {
              handleMenuItemClick(item);
            }}
            data-cy="cloud-waste-menu-anomalies"
          >
            {item.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
